import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Separator } from "@/components/ui/separator";
import { Download, Eye, AlertCircle } from "lucide-react";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { isJobStopping } from "../../models/Jobs/JobActions";
import JobProgress, { hasJobProgress } from "./JobProgress";
import {
  JobRequest,
  isBulkDeletionJob,
  DeleteCaseRequest,
  ExportCasesToCsvRequest,
  DeleteCaseFilesRequest,
  DeleteIntakeFilesRequest,
  DeleteIntakeRequest,
  ExportIntakesToCsvRequest,
  ExportCaseFilesToCsvRequest,
  ExportIntakeFilesToCsvRequest,
  DeleteFirmDocumentsFolderRequest,
  DeleteFirmDocumentsRequest,
  ExportFirmDocumentsFoldersToCsvRequest,
  ExportFirmDocumentsToCsvRequest,
  ExportProvidersToCsvRequest,
  ExportProviderFilesToCsvRequest,
  DeleteProviderRequest,
  DeleteProviderFilesRequest,
  ExportTemplatesToCsvRequest,
  ExportTemplateFilesToCsvRequest,
  DeleteTemplateRequest,
  DeleteTemplateFilesRequest,
  ExportFlaggedPathsToCsvRequest,
  SqlScriptJobRequest,
  isExportSourceLinksToCsvRequest,
} from "../../models/Jobs/JobRequest";
import SourceLinkExportDownloadButton from "../SourceLinks/SourceLinkExportDownloadButton";
import { JobEntityTypeColors } from "../../models/Jobs/JobEntityType";
import { JobType } from "../../models/Jobs/JobType";
import ShortenedPath from "../common/ShortenedPath";

type ChipBreakpoint = "sm" | "md" | "lg";

interface JobInfoViewProps {
  jobRequest: JobRequest;
  size?: ChipBreakpoint;
  /** Show only whether the job was stopped or is stopping, for lists that show its details elsewhere. */
  stopOnly?: boolean;
}

const FailedJobErrorHoverCard: React.FC<{
  error: string;
  retryCount?: number;
}> = ({ error, retryCount }) => {
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Show failure details"
          className="text-destructive hover:text-destructive"
        >
          <AlertCircle className="size-4" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-96 max-h-80 overflow-y-auto p-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold leading-none text-destructive">
              Failure details
            </p>
            {typeof retryCount === "number" && retryCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {retryCount} {retryCount === 1 ? "attempt" : "attempts"}
              </span>
            )}
          </div>
          <Separator />
          <p className="whitespace-pre-wrap break-words font-mono text-xs text-foreground">
            {error}
          </p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const FilePathsHoverCard: React.FC<{
  filePaths: { [key: string]: string };
}> = ({ filePaths }) => {
  const paths = Object.values(filePaths);
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Show file paths for deletion"
          className="text-muted-foreground hover:text-foreground"
        >
          <Eye className="size-4" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-96 max-h-80 overflow-y-auto p-3">
        <div className="space-y-2">
          <p className="text-sm font-semibold leading-none">
            File paths for deletion
          </p>
          <Separator />
          {paths.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">
              No paths recorded
            </p>
          ) : (
            <ul className="space-y-1 break-all font-mono text-xs">
              {paths.map((path, i) => (
                <li key={i}>{path}</li>
              ))}
            </ul>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const ExportParametersHoverCard: React.FC<{
  parameters: Record<string, unknown>;
}> = ({ parameters }) => {
  const entries = Object.entries(parameters);
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Show export query parameters"
          className="text-muted-foreground hover:text-foreground"
        >
          <Eye className="size-4" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-80 p-3">
        <div className="space-y-2">
          <p className="text-sm font-semibold leading-none">
            Export query parameters
          </p>
          <Separator />
          {entries.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">
              No filters applied
            </p>
          ) : (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
              {entries.map(([key, value]) => (
                <React.Fragment key={key}>
                  <dt className="font-medium text-muted-foreground">{key}</dt>
                  <dd className="break-all font-mono">
                    {JSON.stringify(value)}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

/**
 * A deletion driven from an uploaded id list, which has no single target to name.
 */
/*
 * The natural key and file-path chips the per-target renderers show are both empty for one of these: the
 * ids came from a file that can span any number of parents. What the run achieved stands in their place.
 */
const BulkDeletionRequestInfo: React.FC<{ jobRequest: JobRequest }> = ({ jobRequest }) => {
  if (!isBulkDeletionJob(jobRequest)) return null;
  const { bulk } = jobRequest;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>Uploaded list</Badge>
      {bulk.recordsOnly && <Badge variant="warning">Records only</Badge>}
      <Badge variant="neutral">{`${bulk.deletedCount} of ${bulk.totalRows} deleted`}</Badge>
      {bulk.notFoundCount > 0 && (
        <Badge variant="warning">{`${bulk.notFoundCount} not found`}</Badge>
      )}
      {bulk.malformedCount > 0 && (
        <Badge variant="warning">{`${bulk.malformedCount} not an id`}</Badge>
      )}
      {bulk.failedCount > 0 && (
        <Badge variant="danger">{`${bulk.failedCount} failed`}</Badge>
      )}
      {bulk.failuresCsvUrl && (
        <Button asChild variant="ghost" size="icon-sm" aria-label="Download report">
          <a href={bulk.failuresCsvUrl} target="_blank" rel="noreferrer">
            <Download className="size-4" />
          </a>
        </Button>
      )}
    </div>
  );
};

const DeleteCaseRequestInfo: React.FC<{
  jobRequest: DeleteCaseRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  return (
    <div>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`Case Number: ${jobRequest.caseNumber}`}
      </Badge>
    </div>
  );
};
const DeleteCaseFilesRequestInfo: React.FC<{
  jobRequest: DeleteCaseFilesRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  const fileCount = Object.keys(jobRequest.filePaths).length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]} className="max-w-full truncate">
        {`Case Number: ${jobRequest.caseNumber}`}
      </Badge>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`${fileCount} files`}
      </Badge>
      <FilePathsHoverCard filePaths={jobRequest.filePaths} />
    </div>
  );
};
const DeleteProviderRequestInfo: React.FC<{
  jobRequest: DeleteProviderRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  return (
    <div>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`Name Id: ${jobRequest.providerNameId}`}
      </Badge>
    </div>
  );
};
const DeleteProviderFilesRequestInfo: React.FC<{
  jobRequest: DeleteProviderFilesRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  const fileCount = Object.keys(jobRequest.filePaths).length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]} className="max-w-full truncate">
        {`Name Id: ${jobRequest.providerNameId}`}
      </Badge>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`${fileCount} files`}
      </Badge>
      <FilePathsHoverCard filePaths={jobRequest.filePaths} />
    </div>
  );
};

const DeleteTemplateRequestInfo: React.FC<{
  jobRequest: DeleteTemplateRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  return (
    <div>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`Template: ${jobRequest.templateType}`}
      </Badge>
    </div>
  );
};
const DeleteTemplateFilesRequestInfo: React.FC<{
  jobRequest: DeleteTemplateFilesRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  const fileCount = Object.keys(jobRequest.filePaths).length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]} className="max-w-full truncate">
        {`Template type: ${jobRequest.templateType}`}
      </Badge>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`${fileCount} files`}
      </Badge>
      <FilePathsHoverCard filePaths={jobRequest.filePaths} />
    </div>
  );
};

const DeleteIntakeRequestInfo: React.FC<{
  jobRequest: DeleteIntakeRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  return (
    <div>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`Intake Number: ${jobRequest.intakeNumber}`}
      </Badge>
    </div>
  );
};
const DeleteIntakeFilesRequestInfo: React.FC<{
  jobRequest: DeleteIntakeFilesRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  const fileCount = Object.keys(jobRequest.filePaths).length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]} className="max-w-full truncate">
        {`Intake Number: ${jobRequest.intakeNumber}`}
      </Badge>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`${fileCount} files`}
      </Badge>
      <FilePathsHoverCard filePaths={jobRequest.filePaths} />
    </div>
  );
};

const DeleteFirmDocumentsFolderRequestInfo: React.FC<{
  jobRequest: DeleteFirmDocumentsFolderRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  return (
    <div>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`Folder Path: ${jobRequest.folderPath}`}
      </Badge>
    </div>
  );
};

const DeleteFirmDocumentsRequestInfo: React.FC<{
  jobRequest: DeleteFirmDocumentsRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  if (jobRequest.bulk) return <BulkDeletionRequestInfo jobRequest={jobRequest} />;

  const fileCount = Object.keys(jobRequest.filePaths).length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]} className="max-w-full truncate">
        <ShortenedPath folderPath={jobRequest.folderPath} maxLength={20} />
      </Badge>
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {`${fileCount} files`}
      </Badge>
      <FilePathsHoverCard filePaths={jobRequest.filePaths} />
    </div>
  );
};

const GenerateCSVFromCaseRequestInfo: React.FC<{
  jobRequest: ExportCasesToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
    </div>
  );
};

const ExportCaseFilesToCsvRequestInfo: React.FC<{
  jobRequest: ExportCaseFilesToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  const nonNullFilesViewParameters = jobRequest.getNonNullFilesViewParameters();

  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
      <ExportParametersHoverCard parameters={nonNullFilesViewParameters} />
    </div>
  );
};

const GenerateCSVFromProviderRequestInfo: React.FC<{
  jobRequest: ExportProvidersToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
    </div>
  );
};

const ExportProviderFilesToCsvRequestInfo: React.FC<{
  jobRequest: ExportProviderFilesToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  const nonNullFilesViewParameters = jobRequest.getNonNullFilesViewParameters();

  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
      <ExportParametersHoverCard parameters={nonNullFilesViewParameters} />
    </div>
  );
};
const GenerateCSVFromIntakeRequestInfo: React.FC<{
  jobRequest: ExportIntakesToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
    </div>
  );
};

const ExportIntakeFilesToCsvRequestInfo: React.FC<{
  jobRequest: ExportIntakeFilesToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  const nonNullFilesViewParameters = jobRequest.getNonNullFilesViewParameters();

  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
      <ExportParametersHoverCard parameters={nonNullFilesViewParameters} />
    </div>
  );
};

const GenerateCSVFromFirmDocumentsFolderRequestInfo: React.FC<{
  jobRequest: ExportFirmDocumentsFoldersToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
    </div>
  );
};

const ExportFirmDocumentsToCsvRequestInfo: React.FC<{
  jobRequest: ExportFirmDocumentsToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  const nonNullFilesViewParameters = jobRequest.getNonNullFilesViewParameters();

  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
      <ExportParametersHoverCard parameters={nonNullFilesViewParameters} />
    </div>
  );
};

const GenerateCSVFromTemplateRequestInfo: React.FC<{
  jobRequest: ExportTemplatesToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
    </div>
  );
};

const ExportTemplateFilesToCsvRequestInfo: React.FC<{
  jobRequest: ExportTemplateFilesToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  const nonNullFilesViewParameters = jobRequest.getNonNullFilesViewParameters();

  return (
    <div className="flex items-center">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-primary ml-2"
        aria-label="Download CSV"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
      <ExportParametersHoverCard parameters={nonNullFilesViewParameters} />
    </div>
  );
};

const ExportFlaggedPathsToCsvRequestInfo: React.FC<{
  jobRequest: ExportFlaggedPathsToCsvRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const handleDownload = () => {
    if (jobRequest.exportedCsvUrl) {
      window.open(jobRequest.exportedCsvUrl, "_blank");
    }
  };
  return (
    <div className="flex items-center gap-1">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        {jobRequest.exportName}
      </Badge>
      <Button
        aria-label="Download CSV"
        variant="ghost"
        size="icon-sm"
        onClick={handleDownload}
        disabled={!jobRequest.exportedCsvUrl}
      >
        <Download className="size-4" />
      </Button>
    </div>
  );
};

const MAX_REASON_LENGTH = 20;

const SqlScriptJobRequestInfo: React.FC<{
  jobRequest: SqlScriptJobRequest;
  size: ChipBreakpoint;
}> = ({ jobRequest, size }) => {
  const displayedReason = jobRequest.reason.length > MAX_REASON_LENGTH
    ? `${jobRequest.reason.slice(0, MAX_REASON_LENGTH)}...`
    : jobRequest.reason;

  return (
    <div className="flex flex-wrap items-center gap-1 my-1">
      <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>
        Reason: {displayedReason}
      </Badge>
      <HoverCard openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Show SQL script details"
            className="text-muted-foreground hover:text-foreground"
          >
            <Eye className="size-4" />
          </Button>
        </HoverCardTrigger>
        <HoverCardContent
          side="bottom"
          align="end"
          className="w-[640px] max-h-[360px] overflow-y-auto p-3"
        >
          <div className="space-y-3">
            <div className="space-y-1">
              <p className="text-sm font-semibold leading-none">Reason</p>
              <Separator />
              <p className="whitespace-pre-wrap text-xs text-muted-foreground">
                {jobRequest.reason}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold leading-none">SQL Script</p>
              <Separator />
              <pre className="whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs text-foreground">
                {jobRequest.sqlScript}
              </pre>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
};

const renderJobTypeInfo = (
  jobRequest: JobRequest,
  size: ChipBreakpoint
): React.ReactNode => {
  switch (jobRequest.type) {
    case JobType.Delete:
      if (jobRequest instanceof DeleteCaseRequest)
        return <DeleteCaseRequestInfo jobRequest={jobRequest} size={size} />;

      if (jobRequest instanceof DeleteCaseFilesRequest)
        return (
          <DeleteCaseFilesRequestInfo jobRequest={jobRequest} size={size} />
        );
      if (jobRequest instanceof DeleteProviderRequest)
        return (
          <DeleteProviderRequestInfo jobRequest={jobRequest} size={size} />
        );

      if (jobRequest instanceof DeleteProviderFilesRequest)
        return (
          <DeleteProviderFilesRequestInfo jobRequest={jobRequest} size={size} />
        );

      if (jobRequest instanceof DeleteTemplateRequest)
        return (
          <DeleteTemplateRequestInfo jobRequest={jobRequest} size={size} />
        );

      if (jobRequest instanceof DeleteTemplateFilesRequest)
        return (
          <DeleteTemplateFilesRequestInfo jobRequest={jobRequest} size={size} />
        );

      if (jobRequest instanceof DeleteIntakeRequest)
        return <DeleteIntakeRequestInfo jobRequest={jobRequest} size={size} />;

      if (jobRequest instanceof DeleteIntakeFilesRequest)
        return (
          <DeleteIntakeFilesRequestInfo jobRequest={jobRequest} size={size} />
        );

      if (jobRequest instanceof DeleteFirmDocumentsFolderRequest)
        return (
          <DeleteFirmDocumentsFolderRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (jobRequest instanceof DeleteFirmDocumentsRequest)
        return (
          <DeleteFirmDocumentsRequestInfo jobRequest={jobRequest} size={size} />
        );

      break;

    case JobType.ExportCsv:
      if (jobRequest instanceof ExportCasesToCsvRequest)
        return (
          <GenerateCSVFromCaseRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );
      if (jobRequest instanceof ExportCaseFilesToCsvRequest)
        return (
          <ExportCaseFilesToCsvRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (jobRequest instanceof ExportProvidersToCsvRequest)
        return (
          <GenerateCSVFromProviderRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );
      if (jobRequest instanceof ExportProviderFilesToCsvRequest)
        return (
          <ExportProviderFilesToCsvRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (jobRequest instanceof ExportTemplatesToCsvRequest)
        return (
          <GenerateCSVFromTemplateRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );
      if (jobRequest instanceof ExportTemplateFilesToCsvRequest)
        return (
          <ExportTemplateFilesToCsvRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (jobRequest instanceof ExportIntakesToCsvRequest)
        return (
          <GenerateCSVFromIntakeRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );
      if (jobRequest instanceof ExportIntakeFilesToCsvRequest)
        return (
          <ExportIntakeFilesToCsvRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (jobRequest instanceof ExportFirmDocumentsFoldersToCsvRequest)
        return (
          <GenerateCSVFromFirmDocumentsFolderRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (jobRequest instanceof ExportFirmDocumentsToCsvRequest)
        return (
          <ExportFirmDocumentsToCsvRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (jobRequest instanceof ExportFlaggedPathsToCsvRequest)
        return (
          <ExportFlaggedPathsToCsvRequestInfo
            jobRequest={jobRequest}
            size={size}
          />
        );

      if (isExportSourceLinksToCsvRequest(jobRequest))
        return (
          <div className="flex items-center gap-2">
            <Badge variant={JobEntityTypeColors[jobRequest.entityType]}>{jobRequest.exportName}</Badge>
            <SourceLinkExportDownloadButton job={jobRequest} />
          </div>
        );

      break;

    case JobType.SqlScriptExecute:
      if (jobRequest instanceof SqlScriptJobRequest)
        return (
          <SqlScriptJobRequestInfo
            jobRequest={jobRequest}
            size={size}
            />
          );
      break;

    default:
      return null;
  }
  return null;
};

/** Stopped by someone, or stopping: who asked, from the job's row. */
const renderStopInfo = (jobRequest: JobRequest) => {
  if (jobRequest.status === JobRequestStatus.Stopped)
    return (
      <Badge variant="warning">
        {jobRequest.stopRequestedBy ? `Stopped by ${jobRequest.stopRequestedBy}` : "Stopped"}
      </Badge>
    );
  if (isJobStopping(jobRequest))
    return (
      <Badge variant="warning" title={jobRequest.stopRequestedBy ? `Asked by ${jobRequest.stopRequestedBy}` : undefined}>
        Stopping…
      </Badge>
    );
  return null;
};

const JobInfoView: React.FC<JobInfoViewProps> = ({
  jobRequest,
  size = "sm",
  stopOnly = false,
}) => {
  const stopInfo = renderStopInfo(jobRequest);
  if (stopOnly) return stopInfo;

  const typeInfo = renderJobTypeInfo(jobRequest, size);
  const showError =
    jobRequest.status === JobRequestStatus.Failed &&
    !!jobRequest.lastError;

  if (!typeInfo && !showError && !stopInfo && !hasJobProgress(jobRequest)) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {stopInfo}
      {typeInfo}
      <JobProgress job={jobRequest} />
      {showError && (
        <FailedJobErrorHoverCard
          error={jobRequest.lastError as string}
          retryCount={jobRequest.retryCount}
        />
      )}
    </div>
  );
};

export default JobInfoView;
